// services/customPalettes.js

//import axios from 'axios';
import { getCurrentUserId } from './database';
import proxyapi from './ProxyApi';
/**
 * Create a new custom palette
 * 
 * @param {object} paletteData - The palette data including name, theme, colors
 * @returns {Promise<object>} - The created palette
 */
export const createCustomPalette = async (paletteData) => {
  try {
    const userId = await getCurrentUserId();
    
    // Ensure we have both hex and Tailwind values
    const completeData = {
      userId,
      name: paletteData.name,
      theme: paletteData.theme,
      palette: paletteData.palette || {}, // Hex colors
      tailwindPalette: paletteData.tailwindPalette || {} // Tailwind class names
    };
    
    const response = await proxyapi.post('/api/v1/custom-palettes', completeData);
    
    return response.data.palette;
  } catch (error) {
    console.error('Error creating custom palette:', error);
    throw error;
  }
};

/**
 * Get all custom palettes for the current user
 * 
 * @returns {Promise<Array>} - Array of custom palettes
 */
export const getUserCustomPalettes = async () => {
  try {
    const userId = await getCurrentUserId();
    
    const response = await proxyapi.get(`/api/v1/custom-palettes/user/${userId}`);
    
    return response.data.palettes || [];
  } catch (error) {
    console.error('Error fetching custom palettes:', error);
    return [];
  }
};

/**
 * Get a specific custom palette by ID
 * 
 * @param {string} paletteId - The ID of the palette to retrieve
 * @returns {Promise<object>} - The palette data
 */
export const getCustomPalette = async (paletteId) => {
  try {
    const response = await proxyapi.get(`/api/v1/custom-palettes/${paletteId}`);
    
    // Post-process the response to ensure it has a standardized structure
    const palette = response.data.palette;
    if (palette) {
      // Make sure hex colors and tailwind values are properly structured
      if (!palette.palette) palette.palette = {}; // Ensure hex colors exist
      if (!palette.tailwindPalette) palette.tailwindPalette = {}; // Ensure tailwind values exist
    }
    
    return palette;
  } catch (error) {
    console.error('Error fetching custom palette:', error);
    throw error;
  }
};

/**
 * Delete a custom palette
 * 
 * @param {string} paletteId - The ID of the palette to delete
 * @returns {Promise<boolean>} - True if successful
 */
export const deleteCustomPalette = async (paletteId) => {
  try {
    const userId = await getCurrentUserId();
    
    const response = await proxyapi.delete(`/api/v1/custom-palettes/${paletteId}`, {
      data: { userId }
    });
    
    return response.data.status === 'success';
  } catch (error) {
    console.error('Error deleting custom palette:', error);
    throw error;
  }
};

/**
 * Get a custom palette by ID
 * Alias for getCustomPalette with better error handling for UI contexts
 * 
 * @param {string} paletteId - The ID of the palette to retrieve
 * @returns {Promise<object|null>} - The palette data or null if not found
 */
export const getCustomPaletteById = async (paletteId) => {
  try {
    return await getCustomPalette(paletteId);
  } catch (error) {
    console.error('Error fetching custom palette:', error);
    return null;
  }
};

/**
 * Formats a custom palette for storage in user choices
 * 
 * @param {object} palette - The raw palette data from API
 * @returns {object} - Formatted palette colors with hex and tailwind values
 */
export const formatPaletteForStorage = (palette) => {
  if (!palette) return null;
  
  // Create a properly structured palette colors object
  return {
    hex: palette.palette || {}, // Hex color values
    tailwind: palette.tailwindPalette || {} // Tailwind class names
  };
};

export const updateCustomPalette = async (paletteId, paletteData) => {
  try {
    const userId = await getCurrentUserId();
    const response = await proxyapi.put(`/api/v1/custom-palettes/${paletteId}`, {
      userId,
      ...paletteData
    });
    
    if (response.data.status === 'error') {
      throw new Error(response.data.message);
    }
    
    return response.data.palette;
  } catch (error) {
    console.error('Error updating custom palette:', error);
    throw error;
  }
};